import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EventCard } from "@/components/Cards";
import { Calendar, CheckCircle2, Clock3, Eye, FileText, Globe, MapPin, MessageCircle, ShieldCheck, Share2, Smartphone, Ticket, Users, X } from "lucide-react";
import { toast } from "sonner";
import { applyPosterFallback, getEventPoster } from "@/lib/eventDisplay";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [selectedDate, setSelectedDate] = useState("");
  const [posterOpen, setPosterOpen] = useState(false);
  const [creatorProfiles, setCreatorProfiles] = useState([]);

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => {
      setEvent(r.data);
      setQuantities({});
      setSelectedDate(r.data.date || "");
    });
  }, [id]);

  useEffect(() => {
    if (!event) return;
    const params = new URLSearchParams();
    if (event.category) params.set("category", event.category);
    if (event.language) params.set("language", event.language);
    api.get(`/events?${params.toString()}`)
      .then((r) => setSimilar((r.data || []).filter((item) => item.event_id !== event.event_id).slice(0, 6)))
      .catch(() => setSimilar([]));
  }, [event]);

  useEffect(() => {
    if (!event?.recommended_creator_ids?.length) {
      setCreatorProfiles([]);
      return;
    }
    api.get("/creators")
      .then((r) => {
        const wanted = new Set(event.recommended_creator_ids || []);
        setCreatorProfiles((r.data || []).filter((creator) => wanted.has(creator.user_id)));
      })
      .catch(() => setCreatorProfiles([]));
  }, [event?.recommended_creator_ids]);

  if (!event) return <div className="min-h-screen bg-background"><Navbar /></div>;

  const poster = getEventPoster(event);
  const eventUrl = typeof window !== "undefined" ? `${window.location.origin}/event/${event.event_id}` : "";
  const shareText = `${event.title} at ${event.venue}, ${event.city} on ${formatSchedule(event)}. Book on NamasteNetwork.ai`;
  const lowestPrice = Math.min(...(event.prices || []).map((p) => Number(p.price || 0)).filter((price) => price >= 0));
  const hasPublishedPrice = (event.prices || []).length > 0 && Number.isFinite(lowestPrice);
  const ticketsRemaining = Math.max(0, Number(event.tickets_remaining ?? (Number(event.capacity || 0) - Number(event.tickets_sold || 0))));
  const minQty = Math.max(1, Number(event.min_per_order) || 1);
  const maxQty = Math.max(minQty, Number(event.max_per_order) || 8);
  const dateOptions = buildDateOptions(event.date, event.end_date);
  const ticketTiers = (event.prices || []).map((p, index) => resolveTierValidity(p, dateOptions, selectedDate || event.date, index));
  const dayTiers = ticketTiers.filter((p) => p.scope !== "multi_day" && (!p.valid_dates?.length || p.valid_dates.includes(selectedDate || event.date)));
  const passTiers = ticketTiers.filter((p) => p.scope === "multi_day");
  const tierByKey = Object.fromEntries(ticketTiers.map((tier) => [tier.ticket_key, tier]));
  const ticketItems = Object.entries(quantities)
    .map(([key, quantity]) => {
      const { ticketKey, ticketDate } = parseTicketKey(key);
      const tier = tierByKey[ticketKey];
      if (!tier || Number(quantity || 0) <= 0) return null;
      const isPass = tier.scope === "multi_day";
      return {
        key,
        ticket_key: tier.ticket_key,
        category: tier.tier,
        quantity: Number(quantity || 0),
        price: Number(tier.price || 0),
        scope: tier.scope,
        valid_dates: isPass ? tier.valid_dates : [ticketDate || selectedDate || event.date].filter(Boolean),
        scan_policy: tier.scan_policy,
      };
    })
    .filter(Boolean)
    .sort((a, b) => `${a.valid_dates?.[0] || ""}-${a.category}`.localeCompare(`${b.valid_dates?.[0] || ""}-${b.category}`));
  const totalQty = ticketItems.reduce((sum, item) => sum + item.quantity, 0);
  const total = ticketItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const hasPassTickets = ticketItems.some((item) => item.scope === "multi_day");
  const hasSingleDayTickets = ticketItems.some((item) => item.scope !== "multi_day");
  const ticketingMode = event.ticketing_mode || "namaste";
  const namasteTicketingLive = event.status === "approved" && ticketingMode === "namaste" && event.ticketing_approved !== false;
  const externalTicketUrl = event.external_ticket_url || event.eventbrite_url;
  const fallbackExternalTicketing = ticketingMode === "namaste" && !namasteTicketingLive && Boolean(externalTicketUrl);
  const inquiryWhatsapp = String(event.inquiry_whatsapp || "").replace(/[^\d]/g, "");
  const canProceed = totalQty >= minQty && totalQty <= maxQty;
  const updateQty = (tier, delta, ticketDate = selectedDate || event.date) => {
    setQuantities((current) => {
      const key = quantityKey(tier.ticket_key, tier.scope === "multi_day" ? "" : ticketDate);
      const existing = Number(current[key] || 0);
      const currentTotal = Object.values(current).reduce((sum, value) => sum + Number(value || 0), 0);
      const available = ticketRemaining(tier, ticketDate);
      const next = Math.max(0, Math.min(available, existing + delta));
      if (delta > 0 && currentTotal >= maxQty) return current;
      return { ...current, [key]: next };
    });
  };
  const layoutIsImage = event.layout_file && (
    event.layout_file.startsWith("data:image/") ||
    event.layout_file_type?.startsWith("image/") ||
    /\.(jpg|jpeg|png|webp|gif)$/i.test(event.layout_file_name || event.layout_file)
  );
  const shareEvent = async () => {
    if (navigator.share) {
      await navigator.share({ title: event.title, text: shareText, url: eventUrl }).catch(() => {});
      return;
    }
    await navigator.clipboard.writeText(eventUrl);
    toast.success("Event link copied");
  };
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${eventUrl}`)}`;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="relative overflow-hidden border-b border-border">
        <img src={poster} alt="" aria-hidden="true" decoding="async" className="absolute inset-0 w-full h-full object-cover blur-3xl opacity-30" onError={(e) => applyPosterFallback(e, event)} />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/85 to-background" />
        <div className="relative page-shell py-10 md:py-14 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-7">
            <button
              type="button"
              onClick={() => setPosterOpen(true)}
              className="group relative w-full overflow-hidden rounded-2xl border border-border bg-muted text-left"
              title="View full poster"
            >
              <img src={poster} alt={event.title} loading="eager" fetchPriority="high" decoding="async" className="w-full aspect-[16/10] object-cover transition duration-500 group-hover:scale-105" onError={(e) => applyPosterFallback(e, event)} />
              <span className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-black/70 px-3 py-2 text-xs font-bold text-white opacity-0 backdrop-blur transition group-hover:opacity-100">
                <Eye className="h-4 w-4" /> View full poster
              </span>
            </button>
          </div>
          <div className="md:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.3em] font-black text-primary">{event.category} · {event.language}</p>
            <h1 className="font-display text-3xl md:text-5xl font-black mt-2 leading-tight">{event.title}</h1>
            <div className="mt-3 flex flex-wrap gap-2">
              {hasPublishedPrice && <Badge variant="secondary">from £{lowestPrice.toFixed(2)}</Badge>}
              {ticketsRemaining > 0 && <Badge variant="outline">{ticketsRemaining} tickets left</Badge>}
              {namasteTicketingLive && <Badge className="bg-primary text-primary-foreground">Live booking</Badge>}
              {ticketingMode === "external" && <Badge variant="outline">External tickets</Badge>}
              {ticketingMode === "inquiry" && <Badge variant="outline">Inquiry only</Badge>}
              {event.festival_campaign && <Badge variant="outline">{event.festival_campaign}</Badge>}
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <p className="flex items-center gap-2"><Calendar className="h-4 w-4 text-primary" /> {formatSchedule(event)}</p>
              <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> {event.venue}{event.venue_address ? `, ${event.venue_address}` : ""}, {event.city}</p>
              <p className="flex items-center gap-2"><Globe className="h-4 w-4 text-primary" /> {event.language}</p>
            </div>
            <p className="mt-5 text-foreground/90">{event.description}</p>
            {event.schedule_notes && <p className="mt-3 text-sm text-muted-foreground">{event.schedule_notes}</p>}
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={shareEvent} variant="outline" className="rounded-full">
                <Share2 className="mr-2 h-4 w-4" /> Share
              </Button>
              <Button asChild variant="outline" className="rounded-full">
                <a href={whatsappUrl} target="_blank" rel="noreferrer">
                  <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
                </a>
              </Button>
            </div>
            <div className="mt-4 rounded-xl border border-border bg-muted/40 p-4">
              <p className="font-bold">Namaste Network mobile apps are coming soon</p>
              <p className="mt-1 text-sm text-muted-foreground">Our iOS and Android apps are being prepared for launch. You can book and manage tickets on this website in the meantime.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" disabled aria-label="iOS app coming soon">
                  <Smartphone className="mr-2 h-4 w-4" /> iOS app · Coming soon
                </Button>
                <Button size="sm" variant="secondary" disabled aria-label="Android app coming soon">
                  <Smartphone className="mr-2 h-4 w-4" /> Android app · Coming soon
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="page-shell py-10 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          {dateOptions.length > 1 && (
            <div className="rounded-2xl border border-border p-6">
              <h2 className="font-display text-xl font-extrabold flex items-center gap-2 mb-2">
                <Calendar className="h-5 w-5 text-primary" /> Event days
              </h2>
              <p className="text-sm text-muted-foreground">
                {namasteTicketingLive
                  ? "This is a multi-day event. Choose the day you want before booking."
                  : "This event runs across multiple days. Confirm the daily programme with the organiser before attending."}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {dateOptions.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => setSelectedDate(day.value)}
                    className={`rounded-full border px-4 py-2 text-sm font-bold transition ${selectedDate === day.value ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/60"}`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
              {event.schedule_notes && <p className="mt-4 text-sm text-muted-foreground">{event.schedule_notes}</p>}
            </div>
          )}

          {namasteTicketingLive ? (
          <div className="rounded-2xl border border-border p-6">
            <h2 className="font-display text-xl font-extrabold flex items-center gap-2 mb-5">
              <Ticket className="h-5 w-5 text-primary" /> Pick your tickets
            </h2>
            {dayTiers.length > 0 && (
              <>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-sm font-bold">Tickets for {formatShortDate(selectedDate || event.date)}</p>
                  {dateOptions.length > 1 && <p className="text-xs text-muted-foreground">Change day above to view daily tickets.</p>}
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  {dayTiers.map((p) => <TicketTierCard key={`${p.ticket_key}-${selectedDate || event.date}`} tier={p} qty={Number(quantities[quantityKey(p.ticket_key, selectedDate || event.date)] || 0)} updateQty={updateQty} selectedDate={selectedDate || event.date} />)}
                </div>
              </>
            )}
            {passTiers.length > 0 && (
              <div className={dayTiers.length > 0 ? "mt-6" : ""}>
                <p className="mb-3 text-sm font-bold">Weekend and full-event passes</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {passTiers.map((p) => <TicketTierCard key={p.ticket_key} tier={p} qty={Number(quantities[quantityKey(p.ticket_key)] || 0)} updateQty={updateQty} />)}
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className="text-sm font-semibold">Selected tickets: {totalQty}</span>
              <p className="text-xs text-muted-foreground">Allowed per order across all ticket types: {minQty}-{maxQty}</p>
            </div>
            {hasPassTickets && hasSingleDayTickets && (
              <div className="mt-4 rounded-lg border border-amber-400/35 bg-amber-400/10 p-3 text-sm text-foreground">
                Passes cover multiple event days. Add single-day tickets only when they are for different guests.
              </div>
            )}
            {totalQty > 1 && (
              <div className="mt-4 rounded-lg border border-primary/25 bg-primary/10 p-3 text-sm text-foreground">
                This order will create {totalQty} individual QR tickets across {ticketItems.length} ticket type{ticketItems.length > 1 ? "s" : ""}, so guests can enter separately.
              </div>
            )}

            {event.ticket_notes && (
              <div className="mt-5 rounded-lg bg-muted p-4 text-sm text-muted-foreground">
                {event.ticket_notes}
              </div>
            )}
          </div>
          ) : (
            <div className="rounded-2xl border border-border p-6">
              <h2 className="font-display text-xl font-extrabold flex items-center gap-2 mb-3">
                <Ticket className="h-5 w-5 text-primary" /> Tickets
              </h2>
              {(ticketingMode === "external" || fallbackExternalTicketing) && externalTicketUrl ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    {fallbackExternalTicketing
                      ? "Namaste checkout is being prepared for this event. You can use the organiser ticket link meanwhile."
                      : "This organiser is selling tickets through an external ticketing link."}
                  </p>
                  <Button asChild className="mt-5 rounded-full">
                    <a href={externalTicketUrl} target="_blank" rel="noreferrer">{fallbackExternalTicketing ? "Open organiser ticket link" : "Open ticket link"}</a>
                  </Button>
                </>
              ) : ticketingMode === "inquiry" ? (
                <>
                  <p className="text-sm text-muted-foreground">Tickets are handled by direct organiser inquiry for this event.</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {inquiryWhatsapp && <Button asChild className="rounded-full"><a href={`https://wa.me/${inquiryWhatsapp}?text=${encodeURIComponent(`Hi, I am interested in tickets for ${event.title}`)}`} target="_blank" rel="noreferrer">WhatsApp organiser</a></Button>}
                    {event.inquiry_phone && <Button asChild variant="outline" className="rounded-full"><a href={`tel:${event.inquiry_phone}`}>Call organiser</a></Button>}
                    {event.inquiry_email && <Button asChild variant="outline" className="rounded-full"><a href={`mailto:${event.inquiry_email}`}>Email organiser</a></Button>}
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">NamasteNetwork ticketing is waiting for final admin confirmation with the organiser.</p>
              )}
            </div>
          )}

          {event.layout_file && (
            <div className="rounded-2xl border border-border p-6">
              <h2 className="font-display text-xl font-extrabold flex items-center gap-2 mb-4">
                <MapPin className="h-5 w-5 text-primary" /> Venue layout
              </h2>
              {layoutIsImage ? (
                <img src={event.layout_file} alt={event.layout_file_name || "Venue layout"} className="w-full rounded-xl border border-border object-cover" />
              ) : (
                <a href={event.layout_file} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl border border-border p-4 font-bold hover:border-primary">
                  <FileText className="h-5 w-5 text-primary" />
                  Open {event.layout_file_name || "layout file"}
                </a>
              )}
              {event.layout_notes && <p className="mt-3 text-sm text-muted-foreground">{event.layout_notes}</p>}
            </div>
          )}

          {creatorProfiles.length > 0 && (
            <div className="rounded-2xl border border-border p-6">
              <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">Community voices</p>
              <h2 className="mt-1 font-display text-xl font-extrabold">Recommended by</h2>
              <p className="mt-1 text-sm text-muted-foreground">Creators and community pages highlighting this event.</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {creatorProfiles.map((creator) => <EventCreatorCard key={creator.user_id} creator={creator} />)}
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-border p-6">
            <h2 className="font-display text-xl font-extrabold mb-4">Good to know</h2>
            {namasteTicketingLive ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <TrustFact icon={ShieldCheck} title="Secure QR ticket" body="Every ticket has its own scannable code for entry." />
                <TrustFact icon={Users} title="Group friendly" body={`Buy ${minQty}-${maxQty} tickets in one order across ticket types.`} />
                <TrustFact icon={Clock3} title="Fast entry" body="Door staff can scan individual guest tickets separately." />
                <TrustFact icon={CheckCircle2} title="Verified status" body="Your booking status updates after scan, refund or cancellation." />
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <TrustFact icon={Calendar} title="Check the programme" body="Confirm the date and daily programme before travelling." />
                <TrustFact icon={MapPin} title="Check the venue" body="Review the venue address and entry arrangements supplied by the organiser." />
                <TrustFact icon={MessageCircle} title="Organiser admission" body={ticketingMode === "external" ? "Booking and admission are managed by the external ticket provider." : "Admission is arranged directly with the event organiser."} />
                <TrustFact icon={Share2} title="Share the listing" body="Send this event page to friends so everyone has the same current details." />
              </div>
            )}
          </div>
        </div>

        {namasteTicketingLive ? (
        <div className="rounded-2xl border border-border p-6 h-fit sticky top-20">
          <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">Order summary</p>
          <div className="mt-4 space-y-2 text-sm">
            {ticketItems.length === 0 ? (
              <div className="flex justify-between text-muted-foreground"><span>No tickets selected</span><span>£0.00</span></div>
            ) : ticketItems.map((item) => (
              <div key={item.key} className="flex justify-between gap-3">
                <span>
                  {item.category} × {item.quantity}{item.scope === "multi_day" ? " · pass" : ""}
                  <span className="block text-xs text-muted-foreground">{ticketDateLabel(item, selectedDate)}</span>
                </span>
                <span>£{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            {dateOptions.length > 1 && (
              <div className="flex justify-between text-muted-foreground"><span>Selected day</span><span>{formatShortDate(selectedDate)}</span></div>
            )}
            <div className="flex justify-between text-muted-foreground"><span>Booking fee</span><span>Shown at checkout</span></div>
            <div className="border-t border-border pt-3 flex justify-between font-display text-xl font-extrabold">
              <span>Ticket subtotal</span><span>£{total.toFixed(2)}</span>
            </div>
          </div>
          {namasteTicketingLive ? (
          <Button
            onClick={() => navigate(`/booking/event/${event.event_id}`, { state: { ticketItems, tier: ticketItems[0]?.category, qty: totalQty, total, event, selectedDate } })}
            disabled={!canProceed}
            data-testid="proceed-to-pay"
            className="w-full rounded-full mt-5 h-11 font-bold"
          >
            {totalQty === 0 ? "Select tickets" : !canProceed ? `Select ${minQty}-${maxQty} tickets` : "Proceed to pay"}
          </Button>
          ) : (ticketingMode === "external" || fallbackExternalTicketing) && externalTicketUrl ? (
            <Button asChild className="w-full rounded-full mt-5 h-11 font-bold">
              <a href={externalTicketUrl} target="_blank" rel="noreferrer">{fallbackExternalTicketing ? "Open organiser ticket link" : "Open ticket link"}</a>
            </Button>
          ) : (
            <Button disabled className="w-full rounded-full mt-5 h-11 font-bold">Ticketing pending</Button>
          )}
          <div className="mt-4 rounded-xl bg-muted p-3 text-xs text-muted-foreground">
            Tickets appear in My Bookings with individual QR codes after payment. Share one QR with each guest for separate entry.
          </div>
        </div>
        ) : (
          <aside className="h-fit rounded-2xl border border-border p-6 md:sticky md:top-20">
            <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">Admission</p>
            <h2 className="mt-2 font-display text-xl font-extrabold">
              {ticketingMode === "external" ? "External ticketing" : "Contact the organiser"}
            </h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {ticketingMode === "external"
                ? "Tickets and entry confirmation are provided by the organiser's external ticket service."
                : "Admission, prices and daily programme details are managed directly by the organiser."}
            </p>
            {(ticketingMode === "external" || fallbackExternalTicketing) && externalTicketUrl && (
              <Button asChild className="mt-5 w-full rounded-full">
                <a href={externalTicketUrl} target="_blank" rel="noreferrer">Open ticket link</a>
              </Button>
            )}
            {ticketingMode === "inquiry" && (
              <div className="mt-5 grid gap-2">
                {inquiryWhatsapp && <Button asChild className="rounded-full"><a href={`https://wa.me/${inquiryWhatsapp}?text=${encodeURIComponent(`Hi, I am interested in tickets for ${event.title}`)}`} target="_blank" rel="noreferrer">WhatsApp organiser</a></Button>}
                {event.inquiry_phone && <Button asChild variant="outline" className="rounded-full"><a href={`tel:${event.inquiry_phone}`}>Call organiser</a></Button>}
                {event.inquiry_email && <Button asChild variant="outline" className="rounded-full"><a href={`mailto:${event.inquiry_email}`}>Email organiser</a></Button>}
                {!inquiryWhatsapp && !event.inquiry_phone && !event.inquiry_email && (
                  <p className="rounded-xl bg-muted p-3 text-xs text-muted-foreground">Organiser contact details have not been published yet.</p>
                )}
              </div>
            )}
          </aside>
        )}
      </main>

      {similar.length > 0 && (
        <section className="page-shell pb-12">
          <div className="mb-4 flex items-end justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">More like this</p>
              <h2 className="font-display text-2xl font-black">Similar events</h2>
            </div>
            <Button variant="ghost" className="rounded-full" onClick={() => navigate("/")}>Browse all</Button>
          </div>
          <div className="flex gap-5 overflow-x-auto no-scrollbar pb-2">
            {similar.map((item) => <EventCard key={item.event_id} event={item} />)}
          </div>
        </section>
      )}
      {posterOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" role="dialog" aria-modal="true">
          <div className="flex max-h-[94vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
              <div className="min-w-0">
                <p className="truncate font-display text-lg font-black">{event.title}</p>
                <p className="truncate text-xs text-muted-foreground">{event.city} · {formatSchedule(event)}</p>
              </div>
              <Button size="icon" variant="ghost" onClick={() => setPosterOpen(false)} aria-label="Close poster preview">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="min-h-0 overflow-auto bg-black p-3">
              <img src={poster} alt={`${event.title} poster`} decoding="async" className="mx-auto max-h-[80vh] max-w-full rounded-md object-contain" onError={(e) => applyPosterFallback(e, event)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TrustFact({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-border bg-muted/40 p-4">
      <Icon className="h-5 w-5 text-primary" />
      <p className="mt-3 font-bold">{title}</p>
      <p className="mt-1 text-xs text-muted-foreground">{body}</p>
    </div>
  );
}

function EventCreatorCard({ creator }) {
  const navigate = useNavigate();
  const name = creator.display_name || creator.name || "Namaste creator";
  const image = creator.profile_image_url || creator.picture;
  const locations = creator.creator_locations || creator.city_preferences || [];
  const categories = creator.creator_categories || [];
  return (
    <button
      type="button"
      onClick={() => navigate(`/creators/${creator.user_id}`)}
      className="rounded-xl border border-border bg-muted/40 p-4 text-left transition hover:border-primary/50"
    >
      <div className="flex items-start gap-3">
        {image ? (
          <img src={image} alt="" className="h-12 w-12 rounded-full border border-border object-cover" />
        ) : (
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-border bg-background font-black text-primary">
            {name.slice(0, 1)}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate font-bold">{name}</p>
          {creator.instagram_handle && <p className="truncate text-sm font-bold text-primary">{creator.instagram_handle}</p>}
          <p className="mt-1 truncate text-xs text-muted-foreground">{locations.slice(0, 2).join(", ") || "UK"}</p>
        </div>
      </div>
      {categories.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {categories.slice(0, 3).map((item) => <Badge key={item} variant="secondary">{item}</Badge>)}
        </div>
      )}
      {creator.bio && <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{creator.bio}</p>}
    </button>
  );
}

function TicketTierCard({ tier, qty, updateQty, selectedDate }) {
  const remaining = ticketRemaining(tier, selectedDate);
  const soldOut = remaining <= 0;
  return (
    <div
      data-testid={`tier-${tier.tier}`}
      className={`p-4 rounded-2xl border text-left transition ${soldOut ? "border-border bg-muted/50 opacity-70" : qty > 0 ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}
    >
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary">{tier.tier}</p>
      <p className="font-display text-2xl font-black mt-1">£{Number(tier.price || 0).toFixed(2)}</p>
      <p className="text-xs text-muted-foreground">
        {tier.scope === "multi_day" ? `Valid ${tier.valid_dates.length} day${tier.valid_dates.length === 1 ? "" : "s"}` : "Selected day only"}
      </p>
      <p className={`mt-2 text-xs font-bold ${soldOut ? "text-destructive" : "text-emerald-600"}`}>
        {soldOut ? "Sold out" : `${remaining} remaining`}
      </p>
      {tier.description && <p className="mt-2 text-xs text-muted-foreground">{tier.description}</p>}
      <div className="mt-4 flex items-center justify-between rounded-full border border-border">
        <button disabled={qty <= 0} data-testid={`qty-decrement-${tier.ticket_key}`} onClick={() => updateQty(tier, -1, selectedDate)} className="px-4 py-2 font-bold disabled:opacity-40">-</button>
        <span className="px-4 font-bold" data-testid={`qty-value-${tier.tier}`}>{qty}</span>
        <button disabled={soldOut || qty >= remaining} data-testid={`qty-increment-${tier.ticket_key}`} onClick={() => updateQty(tier, 1, selectedDate)} className="px-4 py-2 font-bold disabled:opacity-40">+</button>
      </div>
    </div>
  );
}

function ticketRemaining(tier, selectedDate) {
  const capacity = Math.max(0, Number(tier.capacity || 0));
  if (tier.scope === "multi_day") {
    return Math.max(0, Number(tier.remaining ?? (capacity - Number(tier.sold || 0))));
  }
  const date = String(selectedDate || "").slice(0, 10);
  const remainingByDate = tier.remaining_by_date || {};
  if (date && remainingByDate[date] !== undefined) return Math.max(0, Number(remainingByDate[date]));
  const soldByDate = tier.sold_by_date || {};
  return Math.max(0, capacity - Number(soldByDate[date] ?? tier.sold ?? 0));
}

function quantityKey(ticketKey, ticketDate) {
  return ticketDate ? `${ticketKey}::${ticketDate}` : ticketKey;
}

function parseTicketKey(key) {
  const [ticketKey, ticketDate] = String(key).split("::");
  return { ticketKey, ticketDate };
}

function ticketDateLabel(item, selectedDate) {
  const dates = Array.isArray(item.valid_dates) ? item.valid_dates.filter(Boolean) : [];
  if (item.scope === "multi_day") {
    return dates.length > 1
      ? `Valid ${formatShortDate(dates[0])} to ${formatShortDate(dates[dates.length - 1])}`
      : `Valid ${formatShortDate(dates[0] || selectedDate)}`;
  }
  return `Valid ${formatShortDate(dates[0] || selectedDate)}`;
}

function formatSchedule(event) {
  if (event.end_date) {
    return `${formatShortDate(event.date)} ${event.time || ""} to ${formatShortDate(event.end_date)} ${event.end_time || event.time || ""}`.trim();
  }
  return `${formatShortDate(event.date)} · ${event.time}`;
}

function formatShortDate(value) {
  if (!value) return "";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

function buildDateOptions(start, end) {
  if (!start) return [];
  const startDate = new Date(`${start}T12:00:00`);
  const endDate = new Date(`${end || start}T12:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate < startDate) {
    return [{ value: start, label: start }];
  }
  const days = [];
  const cursor = new Date(startDate);
  while (cursor <= endDate && days.length < 31) {
    days.push({
      value: cursor.toISOString().slice(0, 10),
      label: formatShortDate(cursor.toISOString().slice(0, 10)),
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function resolveTierValidity(tier, dateOptions, selectedDate, index) {
  const rawDates = Array.isArray(tier.valid_dates) ? tier.valid_dates.map((date) => String(date).slice(0, 10)).filter(Boolean) : [];
  const scope = tier.scope || inferTicketScope(tier.tier);
  const validDates = rawDates.length
    ? rawDates
    : scope === "multi_day"
      ? dateOptions.map((day) => day.value)
      : [selectedDate].filter(Boolean);
  return {
    ...tier,
    ticket_key: tier.ticket_key || tier.id || `${tier.tier || "tier"}-${index}`,
    scope,
    valid_dates: validDates,
    scan_policy: tier.scan_policy || (scope === "multi_day" ? "once_per_day" : "once"),
  };
}

function inferTicketScope(name) {
  const label = String(name || "").toLowerCase();
  return /(weekend|full|festival pass|festive pass|season|all days|all-day|all day)/.test(label) ? "multi_day" : "single_day";
}
